import { ProjectLogo } from '../project-logo/project-logo.component';
import dayjs from 'dayjs';
import { WorkflowStatusBadge } from '../../higher-order/workflow-status-badge.component';
import { UserStatusBadge } from '../user-status-badge/user-status-badge.component';
import { RegistrationPolicyBadge } from '../registration-policy-badge/registration-policy-badge.component';
import { NeedsReviewBadge } from '../needs-review-badge/needs-review-badge.component';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { SVGIconComponent } from '../icons/types';
import { PadLock } from '../icons/PadLock.icon';

function Text({
  value,
  maxWidth,
  color,
  tooltip,
  noUppercase
}: {
  value: string | number;
  maxWidth?: string;
  color?: string;
  tooltip?: string;
  noUppercase?: boolean;
}) {
  return maxWidth ? (
    <div
      style={{
        display: 'flex',
        maxWidth: `${maxWidth}`,
        maxHeight: '45px',
        color: color
      }}
      title={tooltip}
      aria-label={tooltip}
    >
      <span style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {typeof value === 'number' && value === 0
          ? value
          : value
          ? typeof value === 'number' || noUppercase
            ? value
            : [value[0].toUpperCase(), value.slice(1)].join('')
          : null}
      </span>
    </div>
  ) : (
    <span style={{ paddingRight: '8px', color: color }} aria-label={tooltip} title={tooltip}>
      {typeof value === 'number' && value === 0
        ? value
        : value
        ? typeof value === 'number' || noUppercase
          ? value
          : [value[0].toUpperCase(), value.slice(1)].join('')
        : null}
    </span>
  );
}

function BoldText({ value, maxWidth }: { value: string; maxWidth?: string }) {
  return maxWidth ? (
    <div
      style={{
        display: 'flex',
        maxWidth: `${maxWidth}`,
        maxHeight: '45px'
      }}
    >
      <span style={{ fontWeight: 'bold', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
        {value}
      </span>
    </div>
  ) : (
    <span style={{ fontWeight: 'bold' }}>{value}</span>
  );
}

function TextWithAlternate({
  value,
  valueAlternate,
  alternateTooltip,
  maxWidth,
  alternateColor
}: {
  value: string;
  valueAlternate: string;
  alternateTooltip?: string;
  maxWidth?: string;
  alternateColor?: string;
}) {
  if (value) {
    return Text({ value: value, maxWidth: maxWidth });
  } else {
    return Text({
      value: valueAlternate,
      maxWidth: maxWidth,
      tooltip: alternateTooltip,
      color: alternateColor ?? '#848484'
    });
  }
}

function PrefixedText({ prefix, text }: { prefix: string; text?: string }) {
  return <span>{text ? `${prefix}${text}` : ''}</span>;
}

function TenantLogo({
  name,
  imageURL,
  align = 'left'
}: {
  imageURL?: string;
  name: string;
  align?: 'left' | 'center' | 'right';
}) {
  return imageURL ? (
    <Box
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : align
      }}
    >
      <ProjectLogo imageURL={imageURL} />
    </Box>
  ) : (
    <span style={{ textAlign: align, display: 'block', paddingRight: '4px', maxWidth: '150px', minHeight: '20px' }}>
      {name}
    </span>
  );
}

function DateTime({ value }: { value?: Date }) {
  const theme = useTheme();
  const isMedScreen = useMediaQuery(theme.breakpoints.only('md'));
  return (
    <>
      {value ? (
        <>
          {dayjs(value).format('DD MMM YYYY')}
          {isMedScreen ? <br /> : ' - '}
          {dayjs(value).format('hh:mma')}
        </>
      ) : (
        ''
      )}
    </>
  );
}

function DateOnly({ value }: { value?: Date }) {
  return <>{value ? dayjs(value).format('DD MMM YYYY') : ''}</>;
}

function ManagedIconCell({ value, tooltipText }: { value?: string; tooltipText?: string }) {
  if (value === undefined) {
    return <></>;
  }
  return (
    <Tooltip title={<span style={{ fontSize: '13px' }}>{tooltipText}</span>} arrow>
      <span>
        <PadLock
          style={{
            opacity: 0.5,
            height: '30px'
          }}
        ></PadLock>
      </span>
    </Tooltip>
  );
}

function SVGCell({
  SVG,
  colorInput,
  iconSize,
  align
}: {
  SVG: SVGIconComponent | undefined;
  colorInput?: string;
  iconSize?: string;
  align?: 'left' | 'center' | 'right';
}) {
  if (SVG === undefined) {
    return <></>;
  }
  return (
    <Box
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : align
      }}
    >
      <SVG
        style={{
          width: iconSize ? iconSize : '34px',
          height: iconSize ? iconSize : '34px',
          color: colorInput ? colorInput : '#000000'
        }}
      />
    </Box>
  );
}
function MultipleSVGCell({
  SVGs,
  colorInput,
  tooltips,
  iconSize,
  align,
  maxVisibleSVGs
}: {
  SVGs: (SVGIconComponent | undefined)[] | undefined;
  colorInput?: string | string[];
  tooltips?: string[]; // Optional label per SVG, shown on hover. Indexes align with SVGs.
  iconSize?: string;
  align?: 'left' | 'center' | 'right';
  maxVisibleSVGs: number; // Maximum number of SVGs to display without showing the count
}) {
  if (!SVGs || SVGs.length === 0) {
    return <></>;
  }
  const displayedSVGs = SVGs.slice(-maxVisibleSVGs).map((SVG, index) => {
    const colorIndex = SVGs.length > maxVisibleSVGs ? SVGs.length - maxVisibleSVGs + index : index;
    const color = Array.isArray(colorInput) ? colorInput[colorIndex] : colorInput || '#000000';
    const tooltip = tooltips?.[colorIndex];
    if (SVG) {
      const icon = (
        <SVG
          style={{
            width: iconSize ? iconSize : '34px',
            height: iconSize ? iconSize : '34px',
            color,
            marginRight: '3px'
          }}
        />
      );
      if (!tooltip) {
        return (
          <span key={index} style={{ display: 'inline-flex' }}>
            {icon}
          </span>
        );
      }
      return (
        <Tooltip key={index} title={<span style={{ fontSize: '13px' }}>{tooltip}</span>} arrow>
          <span style={{ display: 'inline-flex' }}>{icon}</span>
        </Tooltip>
      );
    }
    return null;
  });

  return (
    <div>
      <Box
        style={{
          display: 'flex',
          width: '100%',
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : align
        }}
      >
        {SVGs.length > maxVisibleSVGs && <span style={{ marginRight: '3px' }}>{SVGs.length - maxVisibleSVGs}+</span>}
        {displayedSVGs}
      </Box>
    </div>
  );
}

function EmailMobile({ email, mobile }: { email?: string; mobile?: string }) {
  const emailAddresses = email
    ? email
        .split(',')
        .map((e) => e.trim())
        .filter(Boolean)
    : [];
  const hasContent = emailAddresses.length > 0 || mobile;

  if (!hasContent) {
    return <></>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.5' }}>
      {emailAddresses.length > 0 && (
        <div>
          {emailAddresses.map((addr, index) => (
            <div key={index}>{addr}</div>
          ))}
        </div>
      )}
      {mobile && <div style={{ marginTop: emailAddresses.length > 0 ? '4px' : '0' }}>{mobile}</div>}
    </div>
  );
}

const WorkflowStatus = WorkflowStatusBadge;

const UserStatus = UserStatusBadge;
const RegistrationPolicy = RegistrationPolicyBadge;

/** A cell must render an element, so an unflagged row gets an empty one. */
function NeedsReview({ label, tooltip }: { label?: string; tooltip?: string }) {
  return label ? <NeedsReviewBadge label={label} tooltipLabel={tooltip} /> : <></>;
}

export const TableComponents = {
  Text,
  BoldText,
  PrefixedText,
  TextWithAlternate,
  TenantLogo,
  ManagedIconCell,
  DateTime,
  DateOnly,
  WorkflowStatus,
  UserStatus,
  RegistrationPolicy,
  NeedsReview,
  SVGCell,
  MultipleSVGCell,
  EmailMobile
};
